import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { DateOption } from "@redotech/redo-return-core-common/return-flow/condition-enums";
import type { DateConditionState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { RedoDateRangePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-date-range-picker";
import { RedoDateInput } from "@redotech/redo-web/arbiter-components/input/redo-date-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import {
  dateToPlainDate,
  plainDateToDate,
} from "@redotech/redo-web/timetable/common/utils/date-conversion";
import { memo, useEffect, useMemo, useState } from "react";

export const DateConditionDetails = memo(function DateConditionDetails({
  state,
  setState,
  disabled,
}: ConditionTypeDetailsProps<DateConditionState>) {
  const { t } = useTranslation();
  const [comparison, setComparison] = useState<DateOption | null>(
    state.dateOption,
  );

  const dateOptions: RedoListItem<DateOption>[] = useMemo(
    () =>
      Object.values(DateOption).map((option) => ({
        id: option,
        value: option,
        type: "text" as const,
        text:
          option === DateOption.BEFORE
            ? t("Before or on")
            : option === DateOption.AFTER
              ? t("After or on")
              : t("Between"),
      })),
    [t],
  );

  const selectedOption = comparison
    ? dateOptions.find((option) => option.value === comparison)
    : undefined;

  // For single date
  const valueChange = useHandler((v: Temporal.PlainDate | null) => {
    const date = v && plainDateToDate(v);
    if (date) {
      setState({ ...state, start: date, end: null, dateOption: comparison });
    } else {
      setState({ ...state, start: null, end: null, dateOption: comparison });
    }
  });

  // For date range
  const multiValueChange = useHandler(
    (v: [Temporal.PlainDate, Temporal.PlainDate] | undefined) => {
      if (v) {
        const [start, end] = v;
        const newStart = plainDateToDate(start);
        const newEnd = plainDateToDate(end);
        setState({
          ...state,
          start: newStart,
          end: newEnd,
          dateOption: comparison,
        });
      } else {
        setState({ ...state, start: null, end: null, dateOption: comparison });
      }
    },
  );

  useEffect(() => {
    // If the comparison changes, we need to update the value
    if (state.dateOption !== comparison) {
      if (comparison === DateOption.AFTER || comparison === DateOption.BEFORE) {
        valueChange(state.start ? dateToPlainDate(state.start) : null);
      } else if (state.start && state.end) {
        const start = state.start ? dateToPlainDate(state.start) : null;
        const end = state.end ? dateToPlainDate(state.end) : null;
        if (start && end) {
          multiValueChange([start, end]);
        }
      }
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparison]);

  // Convert Date range to Temporal.PlainDate range for RedoDateRangePicker
  const temporalDateRange:
    | [Temporal.PlainDate, Temporal.PlainDate]
    | undefined = useMemo(() => {
    const start = state.start && dateToPlainDate(state.start);
    const end = state.end && dateToPlainDate(state.end);
    if (start && end) {
      return [start, end];
    }
    return undefined;
  }, [state.start, state.end]);

  return (
    <>
      <RedoSingleSelectDropdownInput
        disabled={disabled}
        flexGrow
        label={t("Comparison")}
        options={dateOptions}
        optionSelected={({ item }) => setComparison(item.value || null)}
        placeholder={t("Select comparison...")}
        selectedItem={selectedOption}
        size="xs"
      />
      {comparison === DateOption.AFTER || comparison === DateOption.BEFORE ? (
        <RedoDateInput
          label={t("Date")}
          setValue={valueChange}
          size="sm"
          state={disabled ? "disabled" : "default"}
          value={state.start ? dateToPlainDate(state.start) : null}
        />
      ) : comparison === DateOption.BETWEEN ? (
        <RedoDateRangePicker
          dateRange={temporalDateRange}
          labelText={t("Date range")}
          setDateRange={multiValueChange}
          size="sm"
        />
      ) : null}
    </>
  );
});
